import {
  AddButtonHeaderCell,
  CloseButton,
  DeleteButtonCell,
  Dialog,
  ExplanationText,
  Modal,
  NumberInput,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableHeaderCell,
  TableRow,
  usePopupOpenState,
} from '@expane/ui'
import { Controller, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { ApplyButton } from 'widgets/Buttons'
import React, { FC, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getSalarySettingServiceThresholdRowErrorMessage,
  getSalarySettingServiceThresholdsExplanationText,
  SalaryGroupServiceSettingsThresholdType,
  sortAndUpdateSalarySettingServiceThresholdFields,
  sortSalarySettingServiceThresholdFields,
  ThresholdInForm,
} from '@expane/logic/salarySettings'
import {
  SalarySettingServiceThresholdDialogDialogProps,
  SalarySettingServiceThresholdFormValues,
} from './logic'
import { useShowPopupOnDirtyFormClose } from 'logic/hooks/popup/useShowPopupOnDirtyFormClose'

const SalarySettingServiceThresholdDialog: FC<SalarySettingServiceThresholdDialogDialogProps> = ({
  closeDialog,
  initialThresholds,
  onChange,
  type,
  currencySymbol,
}) => {
  const { control, handleSubmit, formState, clearErrors, setError } =
    useForm<SalarySettingServiceThresholdFormValues>({
      defaultValues: {
        thresholds: initialThresholds,
      },
    })

  const { t } = useTranslation()

  const {
    fields: thresholdFields,
    remove: removeThresholdField,
    append: appendThresholdFields,
    replace: replaceThresholdFields,
  } = useFieldArray({ control, name: 'thresholds' })

  const watchedThresholdFields = useWatch({
    control,
    name: 'thresholds',
  })

  const submitHandler: SubmitHandler<SalarySettingServiceThresholdFormValues> = async ({
    thresholds,
  }) => {
    for (let i = 0; i < thresholds.length; i += 1) {
      const index = thresholds.findIndex(item => item.qty === thresholds[i].qty)

      if (index !== i) {
        setError(`thresholds.${index}.qty`, { type: 'validate' })
        setError(`thresholds.${i}.qty`, { type: 'validate' })

        return
      }
    }

    onChange(sortSalarySettingServiceThresholdFields(thresholds))
    closeDialog()
  }

  const { closePopups, confirmPopup } = useShowPopupOnDirtyFormClose(formState, closeDialog)

  return (
    <Modal close={closePopups}>
      <div className={'flex items-center justify-between gap-4 mb-4'}></div>

      <Dialog>
        <Dialog.Title>{t('thresholdValues')}</Dialog.Title>
        <Dialog.Body className="w-128">
          <ExplanationText
            type="withoutIcon"
            className="mb-4"
            title={t('salarySettingServiceThresholdsExplanation.title')}
            text={getSalarySettingServiceThresholdsExplanationText(type, t)}
          />
          <TableContainer>
            <TableHeader>
              <tr>
                <TableHeaderCell className="w-60">{t('qty')}</TableHeaderCell>
                <TableHeaderCell className="w-60">
                  {t('value')}, {type === 'fixed' ? currencySymbol : '%'}
                </TableHeaderCell>
                <AddButtonHeaderCell
                  onClick={() => {
                    appendThresholdFields({ qty: '', value: '' })
                  }}
                />
              </tr>
            </TableHeader>
            <TableBody>
              {thresholdFields.map((item, index) => {
                return (
                  <TableRow
                    key={item.id}
                    errorMessage={{
                      isShown:
                        Boolean(formState.errors.thresholds?.[index]?.qty) ||
                        Boolean(formState.errors.thresholds?.[index]?.value),
                      text: getSalarySettingServiceThresholdRowErrorMessage(
                        formState.errors.thresholds?.[index]?.qty?.type,
                        formState.errors.thresholds?.[index]?.value?.type,
                        t,
                      ),
                      reserveIndent: true,
                    }}
                  >
                    <TableCell>
                      <Controller
                        control={control}
                        rules={{
                          min: 1,
                          required: true,
                        }}
                        name={`thresholds.${index}.qty` as 'thresholds.0.qty'}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={e => {
                              clearErrors('thresholds')
                              onChange(e)
                            }}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            allowDecimals={false}
                          />
                        )}
                      />
                    </TableCell>

                    <TableCell>
                      <Controller
                        control={control}
                        name={`thresholds.${index}.value` as 'thresholds.0.value'}
                        rules={{ max: type === 'fixed' ? undefined : 100, required: true }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <NumberInput
                            value={value}
                            onChange={onChange}
                            errorMessage={{
                              isShown: Boolean(error),
                              text: ' ',
                              reserveIndent: false,
                            }}
                            height="small"
                            allowDecimals={false}
                            onBlur={() =>
                              sortAndUpdateSalarySettingServiceThresholdFields(
                                watchedThresholdFields,
                                replaceThresholdFields,
                              )
                            }
                          />
                        )}
                      />
                    </TableCell>

                    {index !== 0 && (
                      <DeleteButtonCell onClick={() => removeThresholdField(index)} />
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </TableContainer>
        </Dialog.Body>
        <Dialog.Footer>
          <ApplyButton
            className="w-fit"
            disabled={!formState.isDirty || formState.isSubmitting}
            spinner={formState.isSubmitting}
            onClick={handleSubmit(submitHandler)}
          />
          <CloseButton onClick={closePopups} />
        </Dialog.Footer>
      </Dialog>
      {confirmPopup}
    </Modal>
  )
}

export const useOpenSalarySettingServiceThresholdDialog = () => {
  const { isOpen, openPopup, closePopup } = usePopupOpenState()

  const initialThresholds = useRef<ThresholdInForm[]>()
  const onChange = useRef<(thresholds: ThresholdInForm[]) => void>()
  const type = useRef<SalaryGroupServiceSettingsThresholdType>('fixed')
  const currencySymbol = useRef<string>()

  const openSalarySettingServiceThresholdDialog = (
    dto: Omit<SalarySettingServiceThresholdDialogDialogProps, 'closeDialog'>,
  ) => {
    initialThresholds.current = dto.initialThresholds
    onChange.current = dto.onChange
    type.current = dto.type
    currencySymbol.current = dto.currencySymbol
    openPopup()
  }

  const closeDialog = () => {
    closePopup()
  }

  const salarySettingServiceThresholdDialog =
    isOpen && initialThresholds.current && onChange.current && currencySymbol.current ? (
      <SalarySettingServiceThresholdDialog
        closeDialog={closeDialog}
        initialThresholds={initialThresholds.current}
        onChange={onChange.current}
        type={type.current}
        currencySymbol={currencySymbol.current}
      />
    ) : null

  return { openSalarySettingServiceThresholdDialog, salarySettingServiceThresholdDialog }
}
